<script lang='ts'>
    import StarRating from './StarRating.svelte';
    import {IconFlag2, IconHeart, IconShare} from '@tabler/icons-svelte';
    import {createEventDispatcher, onMount, tick} from 'svelte';
    import toast from 'svelte-french-toast';
    import UnauthorizedToast from './toasts/UnauthorizedToast.svelte';
    import {clipboard} from '@skeletonlabs/skeleton';
    import EarlyReviewerBadge from "$components/badges/EarlyReviewerBadge.svelte";
    import AggregatedReviewScore from "$components/AggregatedReviewScore.svelte";

    const dispatch = createEventDispatcher();

    export let uuid;
    export let reviewScore;
    export let reviewer;
    export let commentary;
    export let reviewRatings;
    export let livedFrom;
    export let livedUntil;
    export let likes = 0;
    export let likedByCurrentUser = false;
    export let canReport = false;
    export let isHighlighted = false;
    export let url;
    export let isEarlyReview = false;

    let showBreakdown = false;
    $: {
        showBreakdown = commentary && ((commentary?.best_parts?.length || 0) + (commentary?.worst_parts?.length || 0)) <= 100;
    }

    let bestParts = [];
    let worstParts = [];

    $: bestParts = commentary?.best_parts.split("\n")
    $: worstParts = commentary?.worst_parts.split("\n")

    const handleLikeReview = () => {
        if (likedByCurrentUser) {
            dispatch('unlikeReview', {
                uuid,
            });
        } else {
            dispatch('likeReview', {
                uuid,
            });
        }
    };

    const handleReportReview = () => {
        if (!canReport) {
            toast(UnauthorizedToast, {
                position: 'top-center',
                duration: 5000,
                icon: '🔒',
            });
            return;
        }
        
        dispatch('reportReview', {
            uuid,
        });
    };

    const handleShare = () => {
        toast.success('Share link copied to clipboard!', {
            position: 'top-center',
            duration: 2000,
            icon: '📋',
        });
    };

    onMount(async () => {
        await tick();
        setTimeout(() => {
            isHighlighted = false;
        }, 2000);
    });
</script>

<div class='card px-5 py-5 w-full relative transition-all duration-1000 {isHighlighted ? "bg-yellow-50" : "bg-neutral-50"} rounded-lg'>
    <p class="flex flex-row items-center">
        {#if reviewScore}
            <AggregatedReviewScore {reviewScore} />
        {/if}
        {#if isEarlyReview}
                <span class="flex items-center top-1 relative ml-8">
                    <EarlyReviewerBadge />
                </span>
        {/if}
    </p>
    {#if commentary}
        <section class='mt-5'>
            <p class='font-light text-sm text-neutral-500'>Best Part?</p>
            {#each bestParts as part}
                <p class='font-normal mb-3'>{part}</p>
            {/each}
            <p class='font-light text-sm text-neutral-500'>Worst Part?</p>
            {#each worstParts as part}
                <p class='font-normal mb-3'>{part}</p>
            {/each}
        </section>
    {:else}
        <section>
            <p class='font-light text-error-500'>We weren't able to load the review details</p>
        </section>
    {/if}
    {#if showBreakdown}
        <section class='mt-5'>
            {#each reviewRatings as rating}
                {#if rating.rating_types?.label}
                    <div class='w-full flex flex-row justify-between'>
                    <span class='font-light text-neutral-500'>{rating.rating_types.external_label}</span>
                        <div class='inline-block'>
                            <StarRating value={rating.value} maxValue={rating.rating_types.max_value} />
                        </div>
                    </div>
                {/if}
            {/each}
        </section>
    {:else}
        <section class='mt-5 text-center'>
            <button class='text-xs btn btn-sm rounded-lg variant-ghost-tertiary' on:click={() => showBreakdown = true}>Show rating breakdown</button>
        </section>
    {/if}
    <section class='mt-5'>
        <p class='text-xs text-right'>
            {#if reviewer}
                <span class='font-bold'>{reviewer}</span>
            {:else}
                <span class="inline-block h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-12"></span>
            {/if}
            {' '}
            {#if livedFrom >= 0 && livedUntil >= 0}
                {#if livedFrom === livedUntil}
                    <span class='font-light text-neutral-400'>lived here {livedFrom === 0 ? 'before 2015' : livedFrom}</span>
                {:else}
                    <span class='font-light text-neutral-400'>lived here {livedFrom === 0 ? 'before 2015' : livedFrom}-{livedUntil === 0 ? 'before 2015' : livedUntil}</span>
                {/if}
            {/if}
        </p>
    </section>
    <section>
        <div class='border-t-2 mt-5 pt-3 flex flex-row justify-between'>
            <button on:click={handleLikeReview}
                class='btn btn-sm rounded-lg text-xs {likedByCurrentUser ? "variant-ghost-success" : "variant-ringed-success" }'>
                <span class='mr-1'><IconHeart size='13' /></span> Helpful {likes > 0 ? `(${likes})` : ''}
            </button>
            <div class='flex flex-row justify-items-end gap-2'>
                <button on:click={handleShare}
                        class='btn btn-sm rounded-lg text-xs variant-ringed-primary'
                        title='Share this review'
                        use:clipboard={url}>
                    <IconShare size='13' />
                </button>
                <button on:click={handleReportReview}
                        class='btn btn-sm rounded-lg text-xs variant-ringed-warning'>
                    <span class='mr-1'><IconFlag2 size='13' /></span> Report Review
                </button>
            </div>
        </div>
    </section>
</div>
